/* eslint-disable sonarjs/no-duplicate-string */
import React, { Component } from 'react';
import cl from 'classnames';
import PropTypes from 'prop-types';
import copy from '../utils/CopyToClipoard';
import Constants from '../../Constants';
import { tr } from '../utils/locale';
import Tab from '../Tab/Tab';
import CodeSnippet from '../CodeSnippet/CodeSnippet';
import uniqueid from '../uniqueid';
import TrackEventContext from '../utils/TrackEventContext';
import Script from '../utils/Script';

const MAP_CLASSNAME = 'map';

export default class SkeletonDemo extends Component {
    constructor(props) {
        super(props);

        this.state = {
            copied: false,
        };

        this.copyActionIdRequest = uniqueid();
        this.copyActionIdResponse = uniqueid();
    }

    componentWillUnmount() {
        if (this.copyTimeout) {
            clearTimeout(this.copyTimeout);
        }
    }

    serializedParams = () => {
        const { params } = this.props;

        if (!params) {
            return '';
        }

        return Object.entries({ ...params })
            .map(([key, value]) => `${key}=${value}`)
            .join('&\\\n');
    };

    copy = (text) => {
        const { copied } = this.state;
        let textToCopy = text;

        if (textToCopy) {
            textToCopy = textToCopy.replace(Constants.woosmapKey, tr('YOUR_KEY_HERE'));
        }

        if (textToCopy !== null && copy(textToCopy) && !copied) {
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 1000);
        }
    };

    copyRequest = () => {
        this.copy(this.renderCurlCode(true));
    };

    copyResponse = () => {
        const { response } = this.props;
        this.copy(JSON.stringify(response, null, 2));
    };

    getMap = () => {
        const { id } = this.props;
        return document.querySelector(`#${id} .${MAP_CLASSNAME}`);
    };

    renderJsCode = () => {
        const { params, request } = this.props;
        /* eslint-disable */
        return `
    let apiURL = new URL('${request}');

    ${Object.entries({ ...params })
            .map(([key, value]) => `apiURL.searchParams.set('${key}', '${value}');`)
            .join('\n    ')}
    
    let xhr = new XMLHttpRequest();

    xhr.open('GET', apiURL);
    xhr.send();

    xhr.onerror = function() {
      console.log("Request failed");
    };
    xhr.onload = function() {
      console.log('Request API Done', xhr.response);
    };
    `;
    };
    /* eslint-enable */

    renderCurlCode = (removeLineBreak) => {
        const { request, referer, body, method } = this.props;
        let xMethod = '';

        if (method) {
            xMethod = `-X ${method} `;
        }

        let serializedParams = '';

        if (this.serializedParams()) {
            serializedParams = `?\\\n${this.serializedParams()}`;
        }

        let curl = `curl ${xMethod}"${request}${serializedParams}"`;

        if (referer) {
            curl += `\\\n  -H 'referer: ${referer}'`;
        }

        if (body) {
            curl += `\\\n  -d '${body}'`;
        }

        if (removeLineBreak) {
            if (!String.prototype.replaceAll) {
                curl = curl.split('\\\n').join('');
            } else {
                curl = curl.replaceAll('\\\n', '');
            }
        }

        return curl;
    };

    renderHeaderTagline = () => {
        const { headerTagline } = this.props;

        if (!headerTagline) {
            return null;
        }

        return (
            <div className="demo__input-container">
                <p className="demo__label demo__label--wide">{headerTagline}</p>
            </div>
        );
    };

    renderHeaderFilters = () => {
        const { headerFilters } = this.props;

        if (!headerFilters) {
            return null;
        }

        return headerFilters.map(
            (filter) =>
                filter && (
                    <div key={filter.label || filter.key} className={cl('demo__input-container', filter.className)}>
                        {filter.label ? <p className="demo__label">{filter.label}</p> : false}
                        {filter.component}
                    </div>
                )
        );
    };

    renderFooterFilters = () => {
        const { footerFilters } = this.props;

        if (!footerFilters || footerFilters.length === 0) {
            return null;
        }

        return footerFilters.map((filter) => (
            <div className="demo__input-container" key={filter.label || filter.key}>
                {filter.label ? <p className="demo__label demo__label--filter">{filter.label}</p> : false}
                {filter.component}
            </div>
        ));
    };

    render() {
        const { result, request, response, renderCode, withMap, mainContent, className, id } = this.props;
        const { copied } = this.state;

        const filters = this.renderHeaderFilters();
        const filters2 = this.renderFooterFilters();
        const tagLine = this.renderHeaderTagline();

        return (
            <>
                {withMap && <Script url={Constants.woosmapMapSdkUrl} />}
                <article className={cl('demo', className)} id={id}>
                    <div className="demo__content">
                        <div className="demo__showcase">
                            {(tagLine || filters || filters2) && (
                                <div className="demo__filters">
                                    {tagLine}
                                    {filters}
                                    {filters2}
                                </div>
                            )}

                            {withMap && (
                                <div className="demo__map">
                                    <div className={MAP_CLASSNAME} />
                                </div>
                            )}

                            {mainContent && <div className="demo__content">{mainContent}</div>}
                        </div>

                        <div className="demo__data">
                            {result && (
                                <div className="demo__results">
                                    <div className="demo__results__header">{tr('Results')}</div>
                                    <div className="demo__results__content">{result}</div>
                                </div>
                            )}

                            <div className="demo__code">
                                <Tab
                                    actions={[
                                        {
                                            title: tr('Request'),
                                            callback: this.copyRequest,
                                            label: copied ? tr('Copied!') : tr('Copy'),
                                            'data-clipboard-text': tr('Copied!'),
                                            id: this.copyActionIdRequest,
                                            className: copied ? 'copied' : '',
                                        },
                                        {
                                            title: tr('Response'),
                                            callback: this.copyResponse,
                                            label: copied ? tr('Copied!') : tr('Copy'),
                                            'data-clipboard-text': tr('Copied!'),
                                            id: this.copyActionIdResponse,
                                            className: copied ? 'copied' : '',
                                        },
                                    ]}
                                >
                                    {request ? (
                                        <div title={tr('Request')} className="demo-api-request">
                                            <CodeSnippet language="bash" codeString={this.renderCurlCode()} />
                                        </div>
                                    ) : null}

                                    {response ? (
                                        <div title={tr('Response')} className="demo-api-response">
                                            <CodeSnippet
                                                language="json"
                                                codeString={JSON.stringify(response, null, 2)}
                                            />
                                        </div>
                                    ) : null}

                                    {renderCode && (
                                        <div title={tr('Code')} className="demo-api-code">
                                            <CodeSnippet language="javascript" codeString={renderCode()} />
                                        </div>
                                    )}
                                </Tab>
                            </div>
                        </div>
                    </div>
                </article>
            </>
        );
    }
}

SkeletonDemo.contextType = TrackEventContext;

SkeletonDemo.defaultProps = {
    renderCode: null,
    headerFilters: null,
    mainContent: null,
    headerTagline: null,
    footerFilters: null,
    result: null,
    request: null,
    response: {},
    params: {},
    referer: '',
    body: '',
    method: '',
    withMap: false,
    className: '',
    id: '',
};

SkeletonDemo.propTypes = {
    renderCode: PropTypes.func,
    params: PropTypes.object,
    headerTagline: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    headerFilters: PropTypes.array,
    footerFilters: PropTypes.array,
    result: PropTypes.node,
    mainContent: PropTypes.node,
    request: PropTypes.string,
    referer: PropTypes.string,
    withMap: PropTypes.bool,
    body: PropTypes.string,
    method: PropTypes.string,
    response: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    className: PropTypes.string,
    id: PropTypes.string,
};
